import React, { useState, useEffect } from "react";
import { postsService } from "./services/postsService";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

interface ScheduledPost {
  id: string;
  clientId: string;
  postId: string;
  scheduledFor: string;
  status: string;
  attemptCount: number;
  clientTimezone: string;
  maxAttempts: number;
  lastAttemptAt: string | null;
  lastError: any | null;
  preparationExecutionId: string | null;
  publishExecutionId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ScheduledPostsListProps {
  clientId: string;
  onPostClick?: (postId: string) => void;
}

export const ScheduledPostsList: React.FC<ScheduledPostsListProps> = ({
  clientId,
  onPostClick,
}) => {
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [postToCancel, setPostToCancel] = useState<ScheduledPost | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const loadScheduledPosts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const posts = await postsService.getScheduledPosts(clientId);
      setScheduledPosts(posts);
    } catch (err: any) {
      setError(err.message || "Failed to load scheduled posts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadScheduledPosts();

    // Poll for updates every 30 seconds
    const interval = setInterval(loadScheduledPosts, 30000);
    return () => clearInterval(interval);
  }, [clientId]);

  const handleCancelClick = (post: ScheduledPost) => {
    setPostToCancel(post);
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async () => {
    if (!postToCancel) return;

    setIsCancelling(true);
    try {
      await postsService.cancelSchedule(clientId, postToCancel.postId);
      await loadScheduledPosts();
      setShowCancelConfirm(false);
      setPostToCancel(null);
    } catch (err: any) {
      setError(err.message || "Failed to cancel schedule");
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "preparing":
        return "bg-blue-100 text-blue-800";
      case "prepared":
        return "bg-green-100 text-green-800";
      case "publishing":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-200 text-green-900";
      case "error":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "pending":
        return "In attesa";
      case "preparing":
        return "In preparazione";
      case "prepared":
        return "Preparato";
      case "publishing":
        return "In pubblicazione";
      case "completed":
        return "Completato";
      case "error":
        return "Errore";
      case "cancelled":
        return "Annullato";
      default:
        return status;
    }
  };

  const formatDateTime = (dateStr: string, timezone: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading scheduled posts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadScheduledPosts}
          className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (scheduledPosts.length === 0) {
    return (
      <div className="text-center p-8 text-gray-500">
        <p>No scheduled posts</p>
        <p className="text-sm mt-2">
          Schedule posts to publish them automatically at a specific time
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Scheduled Posts</h3>
          <button
            onClick={loadScheduledPosts}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            Refresh
          </button>
        </div>

        {scheduledPosts.map((post) => (
          <div
            key={post.id}
            className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <button
                    onClick={() => onPostClick?.(post.postId)}
                    className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {post.postId}
                  </button>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      post.status
                    )}`}
                  >
                    {getStatusLabel(post.status)}
                  </span>
                </div>

                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <strong>Scheduled:</strong>{" "}
                    {formatDateTime(post.scheduledFor, post.clientTimezone)}
                  </p>
                  <p>
                    <strong>Timezone:</strong> {post.clientTimezone}
                  </p>

                  {post.attemptCount > 0 && (
                    <p>
                      <strong>Attempts:</strong> {post.attemptCount}/
                      {post.maxAttempts}
                    </p>
                  )}

                  {post.lastError && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-sm text-xs">
                      <strong>Error:</strong>{" "}
                      {post.lastError.message || JSON.stringify(post.lastError)}
                    </div>
                  )}
                </div>
              </div>

              <div>
                {post.status !== "completed" &&
                  post.status !== "error" &&
                  post.status !== "cancelled" && (
                    <button
                      onClick={() => handleCancelClick(post)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setPostToCancel(null);
        }}
        onConfirm={handleCancelConfirm}
        title="Cancel Scheduled Post"
        message={`Are you sure you want to cancel the scheduled publication for post ${postToCancel?.postId}?`}
        confirmText="Cancel Schedule"
        variant="warning"
        isLoading={isCancelling}
      />
    </>
  );
};
